import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  Bot,
  Braces,
  CircleHelp,
  Eye,
  FlaskConical,
  Link as LinkIcon,
  Mail,
  MessageCircle,
  LucideIcon,
} from 'lucide-react';
import { ConversationSourceEnum } from '@/Domain/enum/ConversationSource.enum';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';

type Props = {
  source: ConversationSourceEnum;
  colorIconsType?: 'inherit' | 'config';
  fontAwesomeProps?: Partial<FontAwesomeIconProps>;
};

export const ConversationSourceIcon = ({
  source,
  fontAwesomeProps,
  colorIconsType = 'config',
}: Props) => {
  const applyConfigColors = colorIconsType === 'config';
  const { fixedWidth = true, ...restFontAwesomeProps } = fontAwesomeProps || {};
  const iconClassName = classNames('h-3.5 w-3.5 shrink-0 transition-all ease-in duration-300', {
    [`${ConversationHelper.getConversationIconColor(source)}`]: applyConfigColors,
    'scale-[0.78]': source === ConversationSourceEnum.ZENDESK,
  });
  const lucideSources: Partial<Record<ConversationSourceEnum, LucideIcon>> = {
    [ConversationSourceEnum.WIDGET]: MessageCircle,
    [ConversationSourceEnum.API]: Braces,
    [ConversationSourceEnum.PREVIEW]: Eye,
    [ConversationSourceEnum.PUBLIC__LINK]: LinkIcon,
    [ConversationSourceEnum.SIMULATION]: FlaskConical,
    [ConversationSourceEnum.EMAIL]: Mail,
    [ConversationSourceEnum.PUBLIC_QUESTION]: Bot,
  };

  if (ConversationHelper.isCustomConversationSource(source)) {
    const Icon = ConversationHelper.getCustomConversationIcon(source);

    return <Icon className={iconClassName} data-testid="conversation-source-icon" />;
  }

  const LucideSourceIcon = lucideSources[source];
  if (LucideSourceIcon) {
    return <LucideSourceIcon className={iconClassName} data-testid="conversation-source-icon" />;
  }

  // Backend owns the source enum and can ship a value this build doesn't map.
  const fontAwesomeIcon = ConversationHelper.getConversationIcon(source);
  if (!fontAwesomeIcon) {
    return <CircleHelp className={iconClassName} data-testid="conversation-source-icon" />;
  }

  return (
    <FontAwesomeIcon
      icon={fontAwesomeIcon}
      className={iconClassName}
      fixedWidth={fixedWidth}
      {...restFontAwesomeProps}
      data-testid="conversation-source-icon"
    />
  );
};
